import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, Eye, FileText, ExternalLink } from "lucide-react";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";

function StatusBadge({ status }) {
  const isPub = status === "published";
  return (
    <span
      className="px-2 py-0.5 rounded-full text-[11px] font-semibold tracking-wide"
      style={{
        background: isPub ? "rgba(34,197,94,0.12)" : "rgba(255,51,1,0.10)",
        color: isPub ? "#15803d" : "var(--agero-orange)",
      }}
    >
      {isPub ? "Published" : "Draft"}
    </span>
  );
}

export default function AdminBlogList() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/blogs")
      .then((r) => setPosts(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    await api.delete(`/admin/blogs/${id}`);
    load();
  };

  return (
    <main style={{ background: "#EBEBEB", minHeight: "100vh" }}>
      <header className="border-b border-neutral-200/70 bg-white">
        <div className="container-x flex items-center justify-between py-4">
          <Link to="/admin/blogs" className="flex items-center gap-2">
            <img
              src="/main-logo.png"
              alt="LinkedIn Lab"
              className="rounded-lg object-contain"
              style={{ height: 36, width: "auto" }}
            />
            <span className="text-sm font-semibold text-neutral-500">Admin</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/blog"
              target="_blank"
              className="text-[13px] text-neutral-600 hover:text-neutral-900 inline-flex items-center gap-1"
            >
              View public blog <ExternalLink size={12} />
            </Link>
            <span className="text-sm text-neutral-500">{user?.username}</span>
            <button
              onClick={() => {
                logout();
                navigate("/admin/login");
              }}
              className="px-3 py-1.5 rounded-full text-[13px] border border-neutral-200 hover:bg-neutral-50 inline-flex items-center gap-1.5"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <section className="container-x py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1
              className="font-display"
              style={{ fontSize: 44, letterSpacing: "-0.03em" }}
            >
              Posts
            </h1>
            <p className="text-neutral-500 mt-1">
              {posts.length} {posts.length === 1 ? "post" : "posts"}
            </p>
          </div>
          <Link
            to="/admin/blogs/new"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium transition"
            style={{ background: "#0A0A0A", color: "#fff" }}
          >
            <Plus size={16} /> New post
          </Link>
        </div>

        {loading && (
          <div className="py-12 text-center text-neutral-500">Loading...</div>
        )}

        {!loading && posts.length === 0 && (
          <div className="rounded-3xl bg-white border border-neutral-200/70 p-14 text-center">
            <FileText size={36} className="mx-auto text-neutral-300 mb-4" />
            <h3
              className="font-display"
              style={{ fontSize: 22, letterSpacing: "-0.02em" }}
            >
              No posts yet
            </h3>
            <p className="text-neutral-500 mt-2">
              Write your first post to start ranking.
            </p>
            <Link
              to="/admin/blogs/new"
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium"
              style={{ background: "#0A0A0A", color: "#fff" }}
            >
              <Plus size={16} /> Create post
            </Link>
          </div>
        )}

        {!loading && posts.length > 0 && (
          <div className="rounded-2xl bg-white border border-neutral-200/70 overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-neutral-50 text-[12px] uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="px-5 py-3">Title</th>
                  <th className="px-5 py-3 hidden md:table-cell">Status</th>
                  <th className="px-5 py-3 hidden md:table-cell">Updated</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((p) => (
                  <tr key={p.id} className="border-t border-neutral-100">
                    <td className="px-5 py-4">
                      <Link
                        to={`/admin/blogs/${p.id}`}
                        className="font-medium text-neutral-900 hover:text-[color:var(--agero-orange)]"
                      >
                        {p.title}
                      </Link>
                      <div className="text-[12px] text-neutral-500 mt-0.5">/{p.slug}</div>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell">
                      <StatusBadge status={p.status} />
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell text-sm text-neutral-500">
                      {p.updated_at
                        ? new Date(p.updated_at).toLocaleDateString()
                        : ""}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {p.status === "published" && (
                          <Link
                            to={`/blog/${p.slug}`}
                            target="_blank"
                            className="p-2 rounded-lg hover:bg-neutral-100 text-neutral-600"
                            title="View"
                          >
                            <Eye size={15} />
                          </Link>
                        )}
                        <Link
                          to={`/admin/blogs/${p.id}`}
                          className="p-2 rounded-lg hover:bg-neutral-100 text-neutral-600"
                          title="Edit"
                        >
                          <Pencil size={15} />
                        </Link>
                        <button
                          onClick={() => remove(p.id)}
                          className="p-2 rounded-lg hover:bg-red-50 text-red-500"
                          title="Delete"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}
